import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Phone, MessageCircle, Mail, Globe, Share2, QrCode, UserPlus, Download, Building2,
    ExternalLink, ArrowRight, Check, Pencil, Smartphone, HandHeart, ShoppingBag,
} from 'lucide-react';
import './card.css';
import { getTheme } from './themes';
import { SOCIALS, socialHandle, HexMark, CheckBadge } from './components/icons';
import { QRSheet, SaveHintSheet, PromoSheet, ExchangeSheet, shopHref } from './components/Sheets';
import { track } from './lib/track';
import { isIOS, isAndroid, canShare, storage } from './lib/device';
import {
    formatPhone, telHref, smsHref, ensureHttps, prettyUrl, splitName, initials,
    formatDateRange, phoneLabel, emailLabel, inkFor,
} from './lib/format';
import cardRender from '@/assets/hexlink-card.png';

function hexToRgb(hex) {
    const n = parseInt((hex || '#35d6ff').replace('#', ''), 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

function Photo({ card, names }) {
    const [failed, setFailed] = useState(false);
    const src = card.photo?.medium || card.profile?.profilePicture;
    if (!src || failed) {
        return (
            <div className="hx-photo-fallback" role="img" aria-label={names.displayName}>
                {initials(names.displayName)}
            </div>
        );
    }
    return (
        <img
            className="hx-photo"
            src={src}
            srcSet={card.photo ? `${card.photo.small} 192w, ${card.photo.medium} 384w, ${card.photo.large} 768w` : undefined}
            sizes="140px"
            alt={names.displayName}
            width="140"
            height="140"
            fetchpriority="high"
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

export default function CardView({ card, mode = 'live', isOwner = false }) {
    const preview = mode === 'preview';
    const theme = getTheme(card.theme);
    const names = useMemo(() => splitName(card.profile), [card.profile]);
    const profile = card.profile || {};
    const uuid = card.uuid;
    const cardUrl = typeof window !== 'undefined' ? `${window.location.origin}/card/${uuid}` : `/card/${uuid}`;
    const vcardUrl = `/api/card/${uuid}/vcard`;

    const [root, setRoot] = useState(null);
    const [sheet, setSheet] = useState(null); // 'qr' | 'hint' | 'promo' | 'exchange'
    const [toast, setToast] = useState(null);
    const [bioOpen, setBioOpen] = useState(false);
    const [installEvt, setInstallEvt] = useState(null);
    const savedAt = useRef(0);

    track.disabled = preview;

    const accent = card.accentColor || theme.accents[0];
    const style = {
        '--accent': accent,
        '--accent-rgb': hexToRgb(accent),
        '--accent-ink': inkFor(accent),
    };

    const showToast = useCallback((text) => {
        setToast({ text, key: Date.now() });
    }, []);
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(null), 2500);
        return () => clearTimeout(t);
    }, [toast]);

    // Live page: match the browser chrome and overscroll color to the theme.
    useEffect(() => {
        if (preview) return undefined;
        const prev = document.body.style.background;
        document.body.style.background = theme.background;
        let metaTag = document.querySelector('meta[name="theme-color"]');
        if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.name = 'theme-color';
            document.head.appendChild(metaTag);
        }
        metaTag.content = theme.themeColor;
        return () => {
            document.body.style.background = prev;
        };
    }, [preview, theme.background, theme.themeColor]);

    // Owner on Android/desktop Chrome: offer "Install" for their own card.
    useEffect(() => {
        if (!isOwner || preview) return undefined;
        const onPrompt = (e) => {
            e.preventDefault();
            setInstallEvt(e);
        };
        window.addEventListener('beforeinstallprompt', onPrompt);
        return () => window.removeEventListener('beforeinstallprompt', onPrompt);
    }, [isOwner, preview]);

    // After a save, when the visitor comes back from the contact screen, offer them a card of their own (once).
    useEffect(() => {
        if (preview || isOwner) return undefined;
        const maybePromo = () => {
            if (!savedAt.current || document.visibilityState !== 'visible') return;
            if (Date.now() - savedAt.current < 1500) return;
            if (storage('hx:promoSeen')) return;
            storage('hx:promoSeen', String(Date.now()));
            savedAt.current = 0;
            setSheet((s) => s || 'promo');
            track(uuid, 'promo_view', { label: 'post_save' });
        };
        document.addEventListener('visibilitychange', maybePromo);
        const timer = setInterval(maybePromo, 4000);
        return () => {
            document.removeEventListener('visibilitychange', maybePromo);
            clearInterval(timer);
        };
    }, [preview, isOwner, uuid]);

    const onSave = (e) => {
        if (preview) {
            e.preventDefault();
            showToast('Visitors save your contact here');
            return;
        }
        // The server counts save_contact when the .vcf is fetched, so no client event here.
        savedAt.current = Date.now();
        // The link itself opens the contact (iOS) or downloads it (Android). We just add guidance.
        if (isAndroid) setTimeout(() => setSheet('hint'), 900);
        else if (isIOS) showToast('Tap “Create New Contact” to finish');
        else showToast('Contact file downloaded');
    };

    const onShare = async () => {
        track(uuid, 'share');
        const data = {
            title: `${names.displayName} · HexLink`,
            text: [names.displayName, profile.jobTitle, profile.organization].filter(Boolean).join(' · '),
            url: cardUrl,
        };
        if (canShare()) {
            try {
                await navigator.share(data);
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }
        try {
            await navigator.clipboard.writeText(cardUrl);
            showToast('Link copied');
        } catch {
            setSheet('qr');
        }
    };

    const phones = (card.phoneNumbers?.length ? card.phoneNumbers : profile.phone ? [{ number: profile.phone, type: 'mobile', isPrimary: true }] : [])
        .filter((p) => p.isVisible !== false && p.number);
    const emails = (card.emailAddresses?.length ? card.emailAddresses : profile.email ? [{ email: profile.email, type: 'work', isPrimary: true }] : [])
        .filter((e) => e.isVisible !== false && e.email);
    if (card.accountEmail && !emails.some((e) => e.email.toLowerCase() === card.accountEmail.toLowerCase())) {
        emails.push({ email: card.accountEmail, type: 'work' });
    }
    const primaryPhone = phones.find((p) => p.isPrimary) || phones[0];
    const primaryEmail = emails.find((e) => e.isPrimary) || emails[0];
    const socials = Object.entries(card.socialMedia || {})
        .filter(([, v]) => v && String(v).trim())
        .map(([k, v]) => ({ key: k, ...(SOCIALS[k] || SOCIALS.website), href: ensureHttps((SOCIALS[k] || SOCIALS.website).url(String(v).trim())) }));
    const website = socials.find((s) => s.key === 'website');
    const orgs = (card.nonprofitOrganizations || []).filter((n) => n.isVisible !== false && n.name);

    const quick = [
        primaryPhone && { key: 'call', label: 'Call', Icon: Phone, href: telHref(primaryPhone.number) },
        primaryPhone && { key: 'text', label: 'Text', Icon: MessageCircle, href: smsHref(primaryPhone.number) },
        primaryEmail && { key: 'email', label: 'Email', Icon: Mail, href: `mailto:${primaryEmail.email}` },
        website && { key: 'website', label: 'Website', Icon: Globe, href: website.href, external: true },
    ].filter(Boolean);

    const roleOrg = theme.orgOnPatch ? null : profile.organization;
    const sections = []; // keeps the entrance stagger in order
    let i = 4;
    const next = () => i++;

    const { Backdrop, PhotoDecor, HeroTop, HeroAfterName } = theme;

    if (profile.biography) {
        const long = profile.biography.length > 260;
        sections.push(
            <section key="about" className="hx-panel hx-in" style={{ '--i': next() }} aria-labelledby="hx-about">
                <h2 id="hx-about" className="hx-h">About</h2>
                <p className="hx-bio" data-clamped={long && !bioOpen}>
                    {profile.biography}
                </p>
                {long && (
                    <button type="button" className="hx-more" onClick={() => setBioOpen((o) => !o)} aria-expanded={bioOpen}>
                        {bioOpen ? 'Show less' : 'Read more'}
                    </button>
                )}
            </section>,
        );
    }

    if (phones.length || emails.length) {
        sections.push(
            <section key="contact" className="hx-panel hx-in" style={{ '--i': next() }} aria-labelledby="hx-contact">
                <h2 id="hx-contact" className="hx-h">Contact</h2>
                <div className="hx-rows">
                    {phones.map((p) => (
                        <div className="hx-row" key={`p-${p.number}`}>
                            <a className="hx-chip" href={telHref(p.number)} aria-label={`Call ${formatPhone(p.number)}`} onClick={() => track(uuid, 'call')}>
                                <Phone />
                            </a>
                            <a className="hx-row-main" href={telHref(p.number)} onClick={() => track(uuid, 'call')}>
                                <span className="hx-row-title">{formatPhone(p.number)}</span>
                                <span className="hx-row-sub">{phoneLabel(p.type, p.customLabel)}</span>
                            </a>
                            <div className="hx-row-end">
                                <a className="hx-mini" href={smsHref(p.number)} aria-label={`Text ${formatPhone(p.number)}`} onClick={() => track(uuid, 'text')}>
                                    <MessageCircle />
                                </a>
                            </div>
                        </div>
                    ))}
                    {emails.map((e) => (
                        <a className="hx-row" key={`e-${e.email}`} href={`mailto:${e.email}`} onClick={() => track(uuid, 'email')}>
                            <span className="hx-chip">
                                <Mail />
                            </span>
                            <span className="hx-row-main">
                                <span className="hx-row-title">{e.email}</span>
                                <span className="hx-row-sub">{emailLabel(e.type, e.customLabel)}</span>
                            </span>
                        </a>
                    ))}
                </div>
            </section>,
        );
    }

    if (socials.length) {
        sections.push(
            <section key="social" className="hx-panel hx-in" style={{ '--i': next() }} aria-labelledby="hx-social">
                <h2 id="hx-social" className="hx-h">Connect</h2>
                <div className="hx-socials">
                    {socials.map(({ key, label, Icon, href }) => (
                        <a
                            key={key}
                            className="hx-social"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track(uuid, key === 'website' ? 'website' : 'social', { platform: key, link: href })}
                        >
                            <Icon />
                            <span>{key === 'website' ? prettyUrl(href) : label}</span>
                            <span className="hx-sr">{socialHandle(key, href)}</span>
                        </a>
                    ))}
                </div>
            </section>,
        );
    }

    if (profile.organization || orgs.length) {
        sections.push(
            <section key="orgs" className="hx-panel hx-in" style={{ '--i': next() }} aria-labelledby="hx-orgs">
                <h2 id="hx-orgs" className="hx-h">{orgs.length ? 'Organizations' : 'Company'}</h2>
                <div className="hx-rows">
                    {profile.organization && !orgs.some((o) => o.name === profile.organization) && (
                        <div className="hx-row">
                            <span className="hx-chip">{profile.organizationLogo ? <img src={profile.organizationLogo} alt="" /> : <Building2 />}</span>
                            <span className="hx-row-main">
                                <span className="hx-row-title">{profile.organization}</span>
                                {profile.jobTitle && <span className="hx-row-sub">{profile.jobTitle}</span>}
                            </span>
                        </div>
                    )}
                    {orgs.map((o) => {
                        const dates = formatDateRange(o.startDate, o.endDate, o.isCurrent);
                        const inner = (
                            <>
                                <span className="hx-chip">{o.logo ? <img src={o.logo} alt="" /> : <HandHeart />}</span>
                                <span className="hx-row-main">
                                    <span className="hx-row-title">{o.name}</span>
                                    <span className="hx-row-sub">{[o.role, dates].filter(Boolean).join(' · ')}</span>
                                </span>
                                {o.website && <ExternalLink className="hx-row-end" style={{ width: 18, height: 18, color: 'var(--muted)' }} aria-hidden="true" />}
                            </>
                        );
                        return o.website ? (
                            <a
                                key={o.id || o.name}
                                className="hx-row"
                                href={ensureHttps(o.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => track(uuid, 'website', { label: o.name, link: o.website })}
                            >
                                {inner}
                            </a>
                        ) : (
                            <div key={o.id || o.name} className="hx-row">
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </section>,
        );
    }

    if (card.exchangeEnabled !== false) {
        sections.push(
            <button
                key="exchange"
                type="button"
                className="hx-panel hx-exchange hx-in"
                style={{ '--i': next() }}
                onClick={() => {
                    setSheet('exchange');
                    track(uuid, 'exchange_open');
                }}
            >
                <span className="hx-chip">
                    <UserPlus />
                </span>
                <span className="hx-row-main">
                    <span className="hx-exchange-title">Share your info with {names.firstName}</span>
                    <span className="hx-exchange-sub">Swap contacts in two taps</span>
                </span>
                <ArrowRight style={{ width: 20, height: 20, flex: 'none', color: 'var(--accent)' }} aria-hidden="true" />
            </button>,
        );
    }

    return (
        <div ref={setRoot} className="hx-card" data-theme={theme.id} data-mode={mode} style={style}>
            {Backdrop && <Backdrop />}

            <main className="hx-shell">
                <header className="hx-hero">
                    {HeroTop && (
                        <div className="hx-in" style={{ '--i': 0 }}>
                            <HeroTop names={names} profile={profile} />
                        </div>
                    )}
                    <div className="hx-photo-wrap hx-in" style={{ '--i': 0 }}>
                        <Photo card={card} names={names} />
                        {PhotoDecor && <PhotoDecor />}
                    </div>
                    <h1 className="hx-name hx-in" style={{ '--i': 1 }}>
                        {names.displayName}
                        {card.verified && (
                            <span className="hx-verified" title="Verified by HexLink">
                                <CheckBadge aria-hidden="true" />
                                <span className="hx-sr">Verified</span>
                            </span>
                        )}
                    </h1>
                    {names.nickname && (
                        <div className="hx-nick hx-in" style={{ '--i': 1 }}>
                            {names.nickname}
                        </div>
                    )}
                    {HeroAfterName && (
                        <div className="hx-in" style={{ '--i': 2 }}>
                            <HeroAfterName names={names} profile={profile} />
                        </div>
                    )}
                    {(profile.jobTitle || roleOrg) && (
                        <p className="hx-role hx-in" style={{ '--i': 2 }}>
                            {profile.jobTitle}
                            {profile.jobTitle && roleOrg && ' · '}
                            {roleOrg && <strong>{roleOrg}</strong>}
                        </p>
                    )}
                </header>

                {isOwner && !preview && (
                    <div className="hx-owner hx-in" style={{ '--i': 3 }}>
                        <a href="/user/profile">
                            <Pencil />
                            Edit card
                        </a>
                        <button type="button" onClick={() => setSheet('qr')}>
                            <QrCode />
                            My QR
                        </button>
                        {installEvt ? (
                            <button
                                type="button"
                                onClick={async () => {
                                    installEvt.prompt();
                                    await installEvt.userChoice.catch(() => {});
                                    setInstallEvt(null);
                                }}
                            >
                                <Smartphone />
                                Install
                            </button>
                        ) : (
                            isIOS && (
                                <button type="button" onClick={() => showToast('Tap Share, then “Add to Home Screen”')}>
                                    <Smartphone />
                                    Home Screen
                                </button>
                            )
                        )}
                    </div>
                )}

                {quick.length > 0 && (
                    <nav className="hx-quick hx-in" style={{ '--i': 3, '--n': quick.length }} aria-label="Quick actions">
                        {quick.map(({ key, label, Icon, href, external }) => (
                            <a
                                key={key}
                                className="hx-quick-btn"
                                href={href}
                                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                onClick={() => track(uuid, key)}
                            >
                                <Icon aria-hidden="true" />
                                {label}
                            </a>
                        ))}
                    </nav>
                )}

                {sections}

                <aside className="hx-promo hx-in" style={{ '--i': next() }} aria-labelledby="hx-promo-title">
                    <img className="hx-promo-img" src={cardRender} alt="" width="726" height="420" loading="lazy" decoding="async" />
                    <div className="hx-promo-kicker">HexLink by Nordic3D</div>
                    <h2 id="hx-promo-title" className="hx-promo-title">
                        Tap. Saved.
                        <br />
                        Want a card like this?
                    </h2>
                    <p className="hx-promo-text">One tap shares your contact to any phone. No app. Choose your theme and update it anytime.</p>
                    <a
                        className="hx-btn hx-btn-primary"
                        href={shopHref('profile_footer', theme.id)}
                        onClick={() => track(uuid, 'shop_click', { label: 'profile_footer' })}
                    >
                        <ShoppingBag />
                        Get your HexLink
                    </a>
                    <div className="hx-brand">
                        <HexMark />
                        Powered by HexLink
                    </div>
                </aside>
            </main>

            <div className="hx-savebar">
                <div className="hx-savebar-inner">
                    <a className="hx-btn hx-btn-primary hx-save" href={vcardUrl} onClick={onSave} data-testid="save-contact">
                        <Download aria-hidden="true" />
                        Save Contact
                    </a>
                    <button type="button" className="hx-icon-btn" onClick={onShare} aria-label="Share card">
                        <Share2 />
                    </button>
                    <button
                        type="button"
                        className="hx-icon-btn"
                        onClick={() => {
                            setSheet('qr');
                            track(uuid, 'qr_open');
                        }}
                        aria-label="Show QR code"
                    >
                        <QrCode />
                    </button>
                </div>
            </div>

            {toast && (
                <div className="hx-toast" key={toast.key} role="status">
                    <Check aria-hidden="true" />
                    {toast.text}
                </div>
            )}

            {root && (
                <>
                    <QRSheet open={sheet === 'qr'} onClose={() => setSheet(null)} uuid={uuid} cardUrl={cardUrl} isOwner={isOwner} portalTarget={root} />
                    <SaveHintSheet open={sheet === 'hint'} onClose={() => setSheet(null)} vcardUrl={vcardUrl} portalTarget={root} />
                    <PromoSheet open={sheet === 'promo'} onClose={() => setSheet(null)} uuid={uuid} theme={theme.id} portalTarget={root} />
                    <ExchangeSheet
                        open={sheet === 'exchange'}
                        onClose={() => setSheet(null)}
                        uuid={uuid}
                        firstName={names.firstName}
                        preview={preview}
                        portalTarget={root}
                    />
                </>
            )}
        </div>
    );
}
